<script setup lang="ts">
import { computed, unref } from 'vue';
import { uniqDropDownMultiSelectSelection, uniqDropDownMultiSelectValues } from '@nova/core';

import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Dropdown multiselect field type - multiple select from list
 */
const props = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string | boolean | number | string[] | null];
  /** @deprecated Use update:model-value instead */
  // TODO: Remove this event after the migration is complete
  input: [value: string | boolean | number | string[] | null];
}>();

const { normalizedProps } = useCustomFieldProps(props);
const { rules } = useCustomFieldValidation(computed(() => props.field));

const modelValueState = computed(() => uniqDropDownMultiSelectSelection(props.modelValue));

function handleInput(value: string[] | null) {
  const uniqueValue = uniqDropDownMultiSelectValues(value);
  emit('update:model-value', uniqueValue);
  emit('input', uniqueValue);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <v-select
      :model-value="unref(modelValueState)"
      :items="field.dropDownValues || []"
      :rules="rules"
      :readonly="normalizedProps.readOnly"
      :hide-details="normalizedProps.hideDetails"
      :variant="normalizedProps.outlined ? 'outlined' : 'filled'"
      :placeholder="field.placeholder"
      multiple
      chips
      density="comfortable"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
